import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Box, Stack, Heading, Divider } from 'braid-design-system';
import { observer } from 'mobx-react';
import { useEffect } from 'react';

import type { PageConfig } from 'src/libs/PageConfig';

import type { IProspectEmailStore } from '../../../stores/JobApplicationStore/ProspectEmailStore';

import translations from './.vocab/';
import { ProspectEmailListItem } from './ProspectEmailListItem';
import SendProspectEmail from './SendProspectEmail';

import * as styles from './ProspectEmail.css';

interface Props {
  prospectEmailStore: IProspectEmailStore;
  locale: Locale;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

const ProspectEmail = ({
  prospectEmailStore,
  locale,
  addCustomError,
}: Props) => {
  const { t } = useTranslations(translations);

  useEffect(() => {
    prospectEmailStore.loadEmails(addCustomError);
  }, [prospectEmailStore, addCustomError]);

  return (
    <Box marginBottom="medium" className={styles.emailContainer}>
      <Stack space="medium">
        <Heading level="4">{t('New email')}</Heading>
        <Stack space="small">
          <SendProspectEmail
            prospectEmailStore={prospectEmailStore}
            addCustomError={addCustomError}
          />

          {prospectEmailStore.emailCount > 0 ? (
            <Box marginTop="xxsmall">
              <Stack space="medium">
                <Heading level="4">{t('Email history')}</Heading>
                {prospectEmailStore.emailList.map((email, index) => (
                  <Stack space="medium" key={email.id}>
                    <ProspectEmailListItem
                      locale={locale}
                      id={email.id}
                      from={email.from}
                      subject={email.subject}
                      message={email.message}
                      dateSent={email.sentDate}
                      isNew={email.isNew}
                      markAsNotNew={email.markedAsNotNew}
                    />
                    {index < prospectEmailStore.emailList.length ? (
                      <Divider />
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};

export default observer(ProspectEmail);
